import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import type { JobDTO, VpsDTO } from '@devrobot/shared';
import { ArrowUpCircle, KeyRound, RefreshCw, RotateCw, Server, Unplug } from 'lucide-react';
import { ApiError, api, seg } from '../../api/client';
import { qk, useApiMutation, useVps } from '../../api/queries';
import { useUser } from '../../api/user';
import { CapacityBars } from '../../components/CapacityBars';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { CopyButton } from '../../components/CopyButton';
import { ErrorAlert } from '../../components/ErrorAlert';
import { InspectionReportView } from '../../components/InspectionReport';
import { JobStages } from '../../components/JobStages';
import { JobsTable } from '../../components/JobsTable';
import { KeyValue } from '../../components/KeyValue';
import { IconChip, PageHeader, Section } from '../../components/PageHeader';
import { RelativeTime } from '../../components/RelativeTime';
import { PageSkeleton } from '../../components/Skeleton';
import { StatusBadge, StatusDot } from '../../components/StatusBadge';
import { useToast } from '../../components/Toaster';
import { Haptic } from '../../kit/haptics';
import { haptic } from '../../kit/native';
import NotFoundPage from '../NotFoundPage';
import { AllowancesEditor } from './AllowancesEditor';
import { ConnectSshButton, NativeSshSection, ShellSessionsCard } from './ShellAccess';
import { VpsAddressEditor } from './VpsAddressEditor';

type Dialog = 'revoke' | 'remove' | null;

function RemoveVpsDialog({ vps, onClose }: { vps: VpsDTO; onClose: () => void }) {
  const navigate = useNavigate();
  const connected = vps.connection === 'connected' || vps.connection === 'stale';
  const [mode, setMode] = useState<'disconnect' | 'destroy'>('disconnect');
  const remove = useApiMutation((m: 'disconnect' | 'destroy') => api.post<{ job: JobDTO }>(`/api/v1/vps/${seg(vps.id)}/remove`, { mode: m }), { invalidate: [['vps'], ['overview']] });

  return (
    <ConfirmDialog
      title={`Remove ${vps.name} from management?`}
      confirmLabel={mode === 'destroy' ? 'Remove workloads and disconnect' : 'Disconnect management'}
      tone="error"
      size="lg"
      typedConfirmation={vps.name}
      busy={remove.isPending}
      error={remove.error}
      confirmDisabled={mode === 'destroy' && !connected}
      onCancel={onClose}
      onConfirm={() => remove.mutate(mode, { onSuccess: (r) => navigate(`/jobs/${r.job.id}`) })}
    >
      <div className="space-y-2" role="radiogroup" aria-label="Removal mode">
        <label className={`flex cursor-pointer items-start gap-3 rounded-field border p-3 transition-colors ${mode === 'disconnect' ? 'border-primary' : 'border-hairline'}`}>
          <input type="radio" className="radio radio-sm mt-0.5" checked={mode === 'disconnect'} onChange={() => setMode('disconnect')} />
          <span className="text-sm">
            <span className="font-medium">Disconnect management only</span>
            <span className="block text-base-content/70">
              DevRobot stops managing the server and forgets its agent identity. The {vps.targets.length} application{vps.targets.length === 1 ? '' : 's'}, their containers, volumes and data are left untouched and keep running. Clean up manually later if needed.
            </span>
          </span>
        </label>
        <label className={`flex items-start gap-3 rounded-field border p-3 transition-colors ${connected ? 'cursor-pointer' : 'opacity-60'} ${mode === 'destroy' ? 'border-error' : 'border-hairline'}`}>
          <input type="radio" className="radio radio-error radio-sm mt-0.5" checked={mode === 'destroy'} disabled={!connected} onChange={() => setMode('destroy')} />
          <span className="text-sm">
            <span className="font-medium">Remove DevRobot application containers, then disconnect</span>
            <span className="block text-base-content/70">
              The agent stops and removes the containers of every DevRobot application on this server
              {vps.targets.length ? ` (${vps.targets.map((t) => t.stack).join(', ')})` : ''}. Persistent volumes are kept. Containers DevRobot did not create are not touched.
            </span>
            {!connected ? <span className="block dr-ink-error">Unavailable: the agent is not connected, and DevRobot will not claim to remove anything on an unreachable server.</span> : null}
          </span>
        </label>
      </div>
    </ConfirmDialog>
  );
}

export default function VpsDetailPage() {
  const { id = '' } = useParams();
  const user = useUser();
  const toast = useToast();
  const navigate = useNavigate();
  const q = useVps(id);
  const [dialog, setDialog] = useState<Dialog>(null);
  const invalidate = [['vps'], qk.vps(id)];
  const patch = useApiMutation((body: { deploymentsDisabled?: boolean; maintenance?: boolean }) => api.patch<VpsDTO>(`/api/v1/vps/${seg(id)}`, body), { invalidate });
  const refresh = useApiMutation(() => api.post<VpsDTO>(`/api/v1/vps/${seg(id)}/refresh`), { invalidate });
  const enroll = useApiMutation(() => api.post<{ job: JobDTO }>(`/api/v1/vps/${seg(id)}/enroll`), { invalidate });
  const upgrade = useApiMutation(() => api.post<{ job: JobDTO }>(`/api/v1/vps/${seg(id)}/upgrade-agent`), { invalidate });
  const revoke = useApiMutation(() => api.post<VpsDTO>(`/api/v1/vps/${seg(id)}/revoke-identity`), { invalidate });

  if (q.isPending) return <PageSkeleton />;
  if (q.isError) {
    if (q.error instanceof ApiError && q.error.status === 404) return <NotFoundPage />;
    return <ErrorAlert error={q.error} onRetry={() => void q.refetch()} />;
  }
  const v = q.data;
  const admin = user.platformAdmin;
  const connected = v.connection === 'connected' || v.connection === 'stale';
  const stale = v.connection !== 'connected';
  const actionError = refresh.error ?? enroll.error ?? upgrade.error ?? patch.error;
  const onError = () => haptic('error');

  return (
    <>
      <PageHeader
        crumbs={[{ label: 'VPS', to: '/vps' }, { label: v.name }]}
        icon={<IconChip icon={Server} tone="primary" size="lg" />}
        title={v.name}
        badges={
          <>
            <StatusBadge kind="connection" value={v.connection} size="md" />
            <StatusBadge kind="enrollment" value={v.enrollmentState} size="md" />
            {v.deploymentsDisabled ? <span className="badge badge-warning badge-soft">deployments disabled</span> : null}
            {v.maintenance ? <span className="badge badge-warning badge-soft">maintenance</span> : null}
          </>
        }
        subtitle={
          <span className="break-anywhere font-mono text-sm">
            {v.username}@{v.host}:{v.port} · {v.authMethod}
          </span>
        }
        actions={
          <>
            <Haptic disabled={refresh.isPending || !connected}>
              <button
                type="button"
                className="btn btn-sm"
                disabled={refresh.isPending || !connected}
                onClick={() => refresh.mutate(undefined, { onSuccess: () => toast.success('Observation refreshed', { description: `Latest state of ${v.name} from its agent.` }), onError })}
                title={connected ? undefined : 'The agent is not connected'}
              >
                <RefreshCw className={`size-4 ${refresh.isPending ? 'animate-spin' : ''}`} aria-hidden="true" /> Refresh
              </button>
            </Haptic>
            {admin ? <ConnectSshButton vps={v} /> : null}
            {admin && (v.enrollmentState === 'registered' || v.enrollmentState === 'preparation_failed') ? (
              <Haptic disabled={enroll.isPending}>
                <button type="button" className="btn btn-sm btn-primary" disabled={enroll.isPending} onClick={() => enroll.mutate(undefined, { onSuccess: (r) => navigate(`/jobs/${r.job.id}`), onError })}>
                  {enroll.isPending ? <span className="loading loading-spinner loading-xs" aria-hidden="true" /> : <RotateCw className="size-4" aria-hidden="true" />}{' '}
                  {v.enrollmentState === 'registered' ? 'Start enrollment' : 'Retry enrollment'}
                </button>
              </Haptic>
            ) : null}
            {admin && v.enrollmentState === 'ready' && connected ? (
              <Haptic disabled={upgrade.isPending}>
                <button type="button" className="btn btn-sm" disabled={upgrade.isPending} onClick={() => upgrade.mutate(undefined, { onSuccess: (r) => navigate(`/jobs/${r.job.id}`), onError })}>
                  {upgrade.isPending ? <span className="loading loading-spinner loading-xs" aria-hidden="true" /> : <ArrowUpCircle className="size-4" aria-hidden="true" />} Upgrade agent
                </button>
              </Haptic>
            ) : null}
            {admin && v.agent.id ? (
              <button type="button" className="btn btn-sm btn-ghost dr-ink-warning" onClick={() => setDialog('revoke')}>
                <KeyRound className="size-4" aria-hidden="true" /> Revoke identity
              </button>
            ) : null}
            {admin ? (
              <button type="button" className="btn btn-sm btn-soft btn-error" onClick={() => setDialog('remove')}>
                <Unplug className="size-4" aria-hidden="true" /> Remove
              </button>
            ) : null}
          </>
        }
      />
      <ErrorAlert error={actionError} className="mb-4" />

      {v.connection === 'disconnected' || v.connection === 'stale' ? (
        <div role="status" className="alert alert-warning alert-soft mb-6 animate-rise text-sm">
          {v.connection === 'disconnected'
            ? `The agent is disconnected (last observed ${v.observedAt ? new Date(v.observedAt).toLocaleString() : 'never'}). Applications may still be running; states below are the last known values. New deployments to this server are blocked until it reconnects.`
            : 'Observations are stale; values below are the last known state, not live confirmation.'}
        </div>
      ) : null}

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
        <div className="min-w-0 space-y-6 xl:col-span-2">
          <Section title="Assigned applications" description="Deployment targets on this server, with their state and public endpoints.">
            {v.targets.length === 0 ? (
              <p className="text-sm text-base-content/60">No application is deployed here.</p>
            ) : (
              <>
                <ul className="divide-y divide-hairline md:hidden">
                  {v.targets.map((t) => (
                    <li key={t.id} className="space-y-1.5 py-3 first:pt-0 last:pb-0">
                      <Link to={`/targets/${t.id}`} className="link link-hover block font-medium break-anywhere">
                        {t.projectName} / {t.environment}
                      </Link>
                      <div className="font-mono text-xs text-base-content/60">{t.stack}</div>
                      <div className="flex flex-wrap gap-1.5">
                        <StatusBadge kind="process" value={t.process} stale={stale} size="xs" />
                        <StatusBadge kind="health" value={t.health} stale={stale} size="xs" />
                      </div>
                      {t.endpoints.map((e) => (
                        <a key={e} href={e} target="_blank" rel="noreferrer" className="link link-hover block break-anywhere font-mono text-xs">
                          {e}
                        </a>
                      ))}
                    </li>
                  ))}
                </ul>
                <div className="overflow-x-auto max-md:hidden">
                  <table className="table table-sm">
                    <thead>
                      <tr className="text-xs tracking-wide text-base-content/50 uppercase">
                        <th>Application</th>
                        <th>Process</th>
                        <th>Health</th>
                        <th>Endpoints</th>
                      </tr>
                    </thead>
                    <tbody>
                      {v.targets.map((t) => (
                        <tr key={t.id} className="hover:bg-base-200/50">
                          <td>
                            <Link to={`/targets/${t.id}`} className="link link-hover font-medium">
                              {t.projectName} / {t.environment}
                            </Link>
                            <div className="font-mono text-xs text-base-content/60">{t.stack}</div>
                          </td>
                          <td>
                            <StatusBadge kind="process" value={t.process} stale={stale} size="xs" />
                          </td>
                          <td>
                            <StatusBadge kind="health" value={t.health} stale={stale} size="xs" />
                          </td>
                          <td className="text-xs">
                            {t.endpoints.length === 0
                              ? '—'
                              : t.endpoints.map((e) => (
                                  <a key={e} href={e} target="_blank" rel="noreferrer" className="link link-hover block break-anywhere font-mono">
                                    {e}
                                  </a>
                                ))}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </Section>

          <Section
            title="Pending and failed operations"
            actions={
              <Link to={`/jobs?vps=${v.id}`} className="btn btn-ghost btn-sm">
                All jobs on this VPS
              </Link>
            }
          >
            <h3 className="text-xs font-semibold tracking-wide text-base-content/55 uppercase">In progress</h3>
            <JobsTable jobs={v.pendingJobs} emptyTitle="Nothing in progress" />
            <h3 className="text-xs font-semibold tracking-wide text-base-content/55 uppercase">Recently failed</h3>
            <JobsTable jobs={v.failedJobs} emptyTitle="No failed operations" />
          </Section>

          {admin ? <ShellSessionsCard vps={v} /> : null}

          <Section title="Allowed projects and environments">
            <AllowancesEditor vps={v} editable={admin} />
          </Section>

          <Section title="Enrollment">
            <div className="flex flex-wrap items-center gap-2">
              <StatusBadge kind="enrollment" value={v.enrollmentState} />
              {v.enrollmentState === 'awaiting_approval' ? <span className="text-sm text-base-content/65">Open the enrollment job to review the inspection report and approve.</span> : null}
            </div>
            {v.enrollmentError ? (
              <div role="alert" className="alert alert-error alert-soft text-sm">
                {v.enrollmentError}
              </div>
            ) : null}
            <JobStages stages={v.enrollmentSteps} />
            {v.inspection ? (
              <details className="collapse collapse-arrow rounded-field border border-hairline bg-base-200/40">
                <summary className="collapse-title font-medium">Inspection report</summary>
                <div className="collapse-content">
                  <InspectionReportView report={v.inspection} />
                </div>
              </details>
            ) : null}
          </Section>
        </div>

        <div className="min-w-0 space-y-6">
          <Section title="Connection">
            <KeyValue
              items={[
                { label: 'Agent', value: <StatusDot kind="connection" value={v.connection} /> },
                { label: 'Last observed', value: <RelativeTime value={v.observedAt} /> },
                { label: 'Connected since', value: <RelativeTime value={v.connectedAt} fallback="not connected" /> },
                { label: 'Agent version', value: v.agent.version },
                { label: 'Protocol', value: v.agent.protocol },
                { label: 'Driver', value: v.agent.driver },
                { label: 'Agent id', value: v.agent.id ? <span className="font-mono text-xs">{v.agent.id}</span> : null },
                {
                  label: 'Host key',
                  value: v.hostKeyFingerprint ? (
                    <span className="inline-flex items-center gap-1">
                      <span className="break-anywhere font-mono text-xs">{v.hostKeyFingerprint}</span>
                      <CopyButton value={v.hostKeyFingerprint} label="Copy host key fingerprint" />
                    </span>
                  ) : null,
                },
                { label: 'Bootstrap credential', value: v.bootstrapCredentialRetained ? 'retained (encrypted) for recovery' : 'discarded' },
              ]}
            />
          </Section>
          {admin ? <NativeSshSection vps={v} /> : null}
          <Section title="Host">
            <KeyValue
              items={[
                { label: 'Hostname', value: v.host_info.hostname },
                { label: 'OS', value: v.host_info.os ? `${v.host_info.os} ${v.host_info.osVersion ?? ''}` : null },
                { label: 'Kernel', value: v.host_info.kernel },
                { label: 'Architecture', value: v.host_info.arch },
                { label: 'Docker', value: v.docker?.version },
                { label: 'Compose', value: v.docker?.composeVersion },
              ]}
            />
          </Section>
          <Section title="Public address" description="The domain gives every application deployed here a hostname automatically.">
            <VpsAddressEditor vps={v} editable={admin} />
          </Section>
          <Section title="Capacity">
            <CapacityBars capacity={v.capacity} stale={stale && Boolean(v.capacity)} />
          </Section>
          <Section title="Edge proxy">
            <KeyValue
              items={[
                { label: 'Mode', value: v.proxyMode === 'managed-caddy' ? 'managed Caddy' : 'none (host ports only)' },
                {
                  label: 'Status',
                  value: v.edge ? (v.edge.running ? <span className="badge badge-success badge-soft badge-sm">running</span> : <span className="badge badge-error badge-soft badge-sm">not running</span>) : 'not observed',
                },
                { label: 'Ports', value: v.edge ? `${v.edge.httpPort} / ${v.edge.httpsPort}` : null },
                { label: 'Error', value: v.edge?.error ? <span className="dr-ink-error">{v.edge.error}</span> : null, hidden: !v.edge?.error },
              ]}
            />
          </Section>
          <Section title="Deployment controls">
            <label className="flex cursor-pointer items-start gap-3">
              <input type="checkbox" className="toggle toggle-warning mt-0.5" checked={v.deploymentsDisabled} disabled={!admin || patch.isPending} onChange={(e) => patch.mutate({ deploymentsDisabled: e.target.checked }, { onError })} />
              <span>
                <span className="font-medium">Disable new deployments</span>
                <span className="block text-sm text-base-content/65">Blocks new deployments to this server. Running applications keep running and can still be operated.</span>
              </span>
            </label>
            <label className="flex cursor-pointer items-start gap-3">
              <input type="checkbox" className="toggle toggle-warning mt-0.5" checked={v.maintenance} disabled={!admin || patch.isPending} onChange={(e) => patch.mutate({ maintenance: e.target.checked }, { onError })} />
              <span>
                <span className="font-medium">Maintenance mode</span>
                <span className="block text-sm text-base-content/65">Marks the server as under maintenance and blocks deployments. It does not stop anything.</span>
              </span>
            </label>
            {!admin ? <p className="text-xs text-base-content/60">Only platform administrators can change these.</p> : null}
          </Section>
        </div>
      </div>

      {dialog === 'revoke' ? (
        <ConfirmDialog
          title={`Revoke the agent identity of ${v.name}?`}
          confirmLabel="Revoke identity"
          tone="warning"
          typedConfirmation={v.name}
          busy={revoke.isPending}
          error={revoke.error}
          onCancel={() => (setDialog(null), revoke.reset())}
          onConfirm={() => revoke.mutate(undefined, { onSuccess: () => (toast.success('Agent identity revoked', { description: 'Re-enroll the server to manage it again.' }), setDialog(null)) })}
        >
          <p className="text-sm text-base-content/70">The agent is disconnected immediately and its credential stops working. Applications keep running, but DevRobot cannot observe or operate them until the server is enrolled again.</p>
        </ConfirmDialog>
      ) : null}
      {dialog === 'remove' ? <RemoveVpsDialog vps={v} onClose={() => setDialog(null)} /> : null}
    </>
  );
}
